import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import {
  AdditionalData,
  RosterContent,
  RosterEventRepository,
} from '@softapps/pms/roster-core';
import {
  SOFTLINE_API_DELIVERY_NOTE_ROSTER_CONTENT,
  SOFTLINE_API_DELIVERY_NOTE_ROSTER_DELETE_ENTRY,
  SOFTLINE_API_DELIVERY_NOTE_ROSTER_UPDATE_ENTRY,
} from '../delivery-note-roster.api';
import { RosterDeliveryContent } from '../types/roster-delivery-content';

@Injectable()
export class DeliveryNoteRosterEventRepository extends RosterEventRepository<
  RosterContent,
  AdditionalData
> {
  readonly #dataAccess = inject(ApiDataAccess);

  override async loadEvents(
    _employeeId: number,
    beginDate: string,
    endDate: string,
    resourceId: string
  ): Promise<RosterContent[]> {
    const items = await this.#dataAccess.get<
      { additionalData: RosterDeliveryContent['additionalData'] }[]
    >({
      path: SOFTLINE_API_DELIVERY_NOTE_ROSTER_CONTENT,
      queryParams: { beginDate, endDate, resource: resourceId },
    });
    return items.map((item) => {
      const tooltipTitle = item.additionalData.additionalDetail?.length
        ? `${item.additionalData.additionalDetail[0].key} - ${item.additionalData.additionalDetail[0].name ?? ''}`
        : item.additionalData.comment || 'kein Kommentar hinterlegt';
      return {
        id: item.additionalData.id,
        start: item.additionalData.beginTime,
        end: item.additionalData.endTime,
        resource: resourceId,
        title: tooltipTitle,
        additionalData: item.additionalData,
      };
    }) as unknown as RosterContent[];
  }

  override loadEventDetail(): Promise<AdditionalData> {
    throw new Error('Method not implemented.');
  }

  override async createEvent(payload: Partial<RosterContent>): Promise<RosterContent> {
    return this.#dataAccess.create(
      { path: SOFTLINE_API_DELIVERY_NOTE_ROSTER_CONTENT },
      payload
    ) as unknown as RosterContent;
  }

  override async updateEvent(event: RosterContent): Promise<RosterContent> {
    const { id, ...payload } = event;
    return this.#dataAccess.update(
      {
        path: SOFTLINE_API_DELIVERY_NOTE_ROSTER_UPDATE_ENTRY,
        pathParams: { id },
      },
      payload
    ) as unknown as RosterContent;
  }

  override async deleteEvents(events: RosterContent[]): Promise<RosterContent[]> {
    await this.#dataAccess.create(
      { path: SOFTLINE_API_DELIVERY_NOTE_ROSTER_DELETE_ENTRY },
      events.map((e) => e.additionalData.id)
    );
    return events;
  }

  override async deleteEvent(event: RosterContent): Promise<RosterContent> {
    const results = await this.deleteEvents([event]);
    return results[0];
  }

  override async delete(id: number): Promise<void> {
    await this.#dataAccess.create(
      { path: SOFTLINE_API_DELIVERY_NOTE_ROSTER_DELETE_ENTRY },
      [id]
    );
  }
}
